// Re-export types
export type { ThemeTypography, ComponentThemeStyles } from './types';

// Import individual themes
import { standardTheme } from './standard';
import { modernTheme } from './modern';
import { boldTheme } from './bold';
import { elegantTheme } from './elegant';
import { playfulTheme } from './playful';
import { darkTheme } from './dark';
import { gradientTheme } from './gradient';
import { minimalTheme } from './minimal';
import type { ComponentThemeStyles } from './types';

// Export individual themes for direct import
export {
  standardTheme,
  modernTheme,
  boldTheme,
  elegantTheme,
  playfulTheme,
  darkTheme,
  gradientTheme,
  minimalTheme,
};

// Theme registry
export const themeStyles: Record<string, ComponentThemeStyles> = {
  standard: standardTheme,
  modern: modernTheme,
  bold: boldTheme,
  elegant: elegantTheme,
  playful: playfulTheme,
  dark: darkTheme,
  gradient: gradientTheme,
  minimal: minimalTheme,
};

// Helper function to get theme styles with fallback
export const getThemeStyles = (
  theme: string = 'standard',
  customColors?: Record<string, Record<string, string>>
): ComponentThemeStyles => {
  const baseTheme = themeStyles[theme] || themeStyles.standard;

  // If no custom colors, return the base theme
  if (!customColors) {
    return baseTheme;
  }

  // Apply custom colors to the theme
  const customizedTheme = { ...baseTheme };

  Object.keys(customColors).forEach(componentKey => {
    // Apply custom colors whether the component exists in base theme or not
    const existingComponentTheme = customizedTheme[componentKey as keyof ComponentThemeStyles];
    const mergedComponentTheme = {
      ...(existingComponentTheme || {}),
      ...customColors[componentKey],
    } as any;

    // If a custom hero background is provided but overlay/pattern are not explicitly
    // provided, do not inherit visual tint/pattern from the selected base theme.
    // This keeps brand-package solid backgrounds rendering as exact colours.
    if (
      componentKey === 'hero' &&
      customColors.hero?.background &&
      !Object.prototype.hasOwnProperty.call(customColors.hero, 'overlay')
    ) {
      mergedComponentTheme.overlay = '';
    }
    if (
      componentKey === 'hero' &&
      customColors.hero?.background &&
      !Object.prototype.hasOwnProperty.call(customColors.hero, 'pattern')
    ) {
      mergedComponentTheme.pattern = false;
    }

    customizedTheme[componentKey as keyof ComponentThemeStyles] = mergedComponentTheme;
  });

  return customizedTheme;
};
